import React, { useState, useEffect } from 'react';
import { X, Star, MapPin, Phone, Mail, Globe, CheckCheck, Calendar, TrendingUp, Users } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const UserDetailsPanel = ({ conversation, onClose }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (conversation?.participants?.[0]) {
      fetchUserDetails(conversation?.participants?.[0]?.user_profiles);
    }
  }, [conversation]);

  const fetchUserDetails = async (userProfile) => {
    try {
      setLoading(true);
      
      // Get partner details with additional information
      const { data: partnerData, error } = await supabase?.from('partners')?.select(`
          *,
          partner_musical_specialties (
            id,
            specialty
          )
        `)?.eq('user_id', userProfile?.id)?.single();

      if (error && error?.code !== 'PGRST116') {
        console.error('Error fetching partner details:', error);
      }

      // Get agent assignments if user is an artist
      let agentAssignments = [];
      if (partnerData && partnerData?.partner_type !== 'agent') {
        const { data: assignments } = await supabase?.from('agent_assignments')?.select(`
            *,
            agent:partners!agent_id (
              id,
              business_name,
              user_id,
              user_profiles (
                full_name,
                avatar_url
              )
            )
          `)?.eq('artist_id', partnerData?.id);
        
        agentAssignments = assignments || [];
      }

      // Get influencer followers if user is an artist
      let influencerFollows = [];
      if (partnerData && partnerData?.partner_type !== 'agent') {
        const { data: follows } = await supabase?.from('influencer_follows')?.select(`
            *,
            influencer:partners!influencer_id (
              id,
              business_name,
              user_id,
              user_profiles (
                full_name,
                avatar_url
              )
            )
          `)?.eq('artist_id', partnerData?.id);
        
        influencerFollows = follows || [];
      }

      setUserDetails({
        userProfile,
        partner: partnerData,
        agentAssignments,
        influencerFollows
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-80 bg-white dark:bg-neutral-900 border-l border-gray-200 dark:border-neutral-800 p-6 transition-colors">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-neutral-800 rounded w-3/4 mb-4"></div>
          <div className="h-20 bg-gray-200 dark:bg-neutral-800 rounded-full w-20 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-neutral-800 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-neutral-800 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const { userProfile, partner, agentAssignments = [], influencerFollows = [] } = userDetails || {};

  return (
    <div className="w-80 bg-white dark:bg-neutral-900 border-l border-gray-200 dark:border-neutral-800 flex flex-col transition-colors">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-neutral-800 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-neutral-100">Détails du profil</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 dark:text-neutral-400 hover:text-gray-600 dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* User Info */}
        <div className="text-center">
          <img
            src={userProfile?.avatar_url || `/api/placeholder/80/80`}
            alt={userProfile?.full_name}
            className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-white shadow-lg"
          />
          <h4 className="font-semibold text-lg text-gray-900 dark:text-neutral-100 flex items-center justify-center">
            {userProfile?.full_name}
            {partner?.is_verified && (
              <CheckCheck className="w-4 h-4 text-blue-500 ml-2" />
            )}
          </h4>
          <p className="text-gray-500 dark:text-neutral-400 text-sm">
            {partner?.business_name || userProfile?.role || 'Utilisateur'}
          </p>
          {partner?.average_rating > 0 && (
            <div className="flex items-center justify-center mt-2">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600 dark:text-neutral-400 ml-1">
                {partner?.average_rating} / 5
              </span>
            </div>
          )}
        </div>

        {/* Partner Type Badge */}
        {partner && (
          <div className="text-center">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              partner?.partner_type === 'agent' ? 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300' :
              partner?.partner_type === 'venue' ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300' :
              partner?.partner_type === 'booker'? 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300' : 'bg-gray-100 text-gray-800 dark:bg-neutral-700 dark:text-neutral-200'
            }`}>
              {partner?.partner_type === 'agent' ? '🎵 Agent' :
               partner?.partner_type === 'venue' ? '🏛️ Lieu' :
               partner?.partner_type === 'booker'? '📅 Booker' : '👤 Partenaire'}
            </span>
          </div>
        )}

        {/* Contact Information */}
        <div className="space-y-3">
          <h5 className="font-medium text-gray-900 dark:text-neutral-100">Contact</h5>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <Mail className="w-4 h-4 text-gray-400 dark:text-neutral-500 mr-3" />
              <span className="text-gray-600 dark:text-neutral-400">{userProfile?.email || 'Non renseigné'}</span>
            </div>
            {partner?.phone && (
              <div className="flex items-center text-sm">
                <Phone className="w-4 h-4 text-gray-400 dark:text-neutral-500 mr-3" />
                <span className="text-gray-600 dark:text-neutral-400">{partner?.phone}</span>
              </div>
            )}
            {partner?.website_url && (
              <div className="flex items-center text-sm">
                <Globe className="w-4 h-4 text-gray-400 dark:text-neutral-500 mr-3" />
                <a
                  href={partner?.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Site web
                </a>
              </div>
            )}
            {partner?.city && (
              <div className="flex items-center text-sm">
                <MapPin className="w-4 h-4 text-gray-400 dark:text-neutral-500 mr-3" />
                <span className="text-gray-600 dark:text-neutral-400">
                  {partner?.city}{partner?.country && `, ${partner?.country}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Business Stats */}
        {partner && (
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900 dark:text-neutral-100">Statistiques</h5>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-neutral-800 rounded-lg p-3 text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                  {partner?.total_bookings || 0}
                </div>
                <div className="text-xs text-gray-500 dark:text-neutral-400">Réservations</div>
              </div>
              <div className="bg-gray-50 dark:bg-neutral-800 rounded-lg p-3 text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                  {partner?.total_earnings ? `€${Math.round(partner?.total_earnings)}` : '€0'}
                </div>
                <div className="text-xs text-gray-500 dark:text-neutral-400">Revenus</div>
              </div>
            </div>
          </div>
        )}

        {/* Agent Assignments */}
        {agentAssignments?.length > 0 && (
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900 dark:text-neutral-100 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Agents assignés ({agentAssignments?.length})
            </h5>
            <div className="space-y-2">
              {agentAssignments?.map((assignment) => (
                <div key={assignment?.id} className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-neutral-800 rounded-lg">
                  <img
                    src={assignment?.agent?.user_profiles?.avatar_url || `/api/placeholder/32/32`}
                    alt={assignment?.agent?.user_profiles?.full_name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                      {assignment?.agent?.user_profiles?.full_name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-neutral-400">
                      {assignment?.specialization || 'Agent musical'}
                    </p>
                  </div>
                  {assignment?.is_primary && (
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Influencer Followers */}
        {influencerFollows?.length > 0 && (
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900 dark:text-neutral-100 flex items-center">
              <TrendingUp className="w-4 h-4 mr-2" />
              Influenceurs ({influencerFollows?.length})
            </h5>
            <div className="space-y-2">
              {influencerFollows?.map((follow) => (
                <div key={follow?.id} className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-neutral-800 rounded-lg">
                  <img
                    src={follow?.influencer?.user_profiles?.avatar_url || `/api/placeholder/32/32`}
                    alt={follow?.influencer?.user_profiles?.full_name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-neutral-100 truncate">
                      {follow?.influencer?.user_profiles?.full_name}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 dark:text-neutral-400">
                        Engagement: {follow?.engagement_score || 0}%
                      </span>
                      {follow?.is_verified && (
                        <CheckCheck className="w-3 h-3 text-blue-500" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        {partner?.description && (
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900 dark:text-neutral-100">À propos</h5>
            <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">
              {partner?.description}
            </p>
          </div>
        )}

        {/* Member since */}
        {userProfile?.created_at && (
          <div className="pt-4 border-t border-gray-200 dark:border-neutral-800">
            <div className="flex items-center text-sm text-gray-500 dark:text-neutral-500">
              <Calendar className="w-4 h-4 mr-2" />
              Membre depuis {new Date(userProfile.created_at)?.toLocaleDateString('fr-FR', {
                month: 'long',
                year: 'numeric'
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailsPanel;